import { API_BASE } from '../config.js';
import { createPageShell } from '../ui/pageShell.js';
import { el } from '../ui/dom.js';
import { enhanceMenuSelect } from '../ui/menuSelect.js';
import { getCurrentUser } from './session.js';
import type { AdminUserDriver, AdminUserView, UserRole } from '@multirx/shared/types/user.js';

const ROLE_LABELS: Record<UserRole, string> = {
  admin: 'Admin',
  moderator: 'Modérateur',
  player: 'Joueur',
};

const ROLES: UserRole[] = ['admin', 'moderator', 'player'];

async function fetchUsers(query = ''): Promise<AdminUserView[]> {
  const q = query.trim();
  const path = q
    ? `${API_BASE}/api/auth/users?q=${encodeURIComponent(q)}`
    : `${API_BASE}/api/auth/users`;
  const res = await fetch(path);
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error((err as { error?: string }).error ?? 'Erreur lors du chargement des comptes');
  }
  return res.json() as Promise<AdminUserView[]>;
}

async function updateUserRole(id: number, role: UserRole): Promise<AdminUserView> {
  const res = await fetch(`${API_BASE}/api/auth/users/${id}/role`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ role }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error((err as { error?: string }).error ?? 'Erreur lors du changement de rôle');
  }
  return res.json() as Promise<AdminUserView>;
}

async function updateUserBanned(id: number, banned: boolean): Promise<AdminUserView> {
  const res = await fetch(`${API_BASE}/api/auth/users/${id}/ban`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ banned }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error((err as { error?: string }).error ?? 'Erreur lors du ban');
  }
  return res.json() as Promise<AdminUserView>;
}

async function updateUserPassword(id: number, password: string): Promise<void> {
  const res = await fetch(`${API_BASE}/api/auth/users/${id}/password`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ password }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(
      (err as { error?: string }).error ?? 'Erreur lors du changement de mot de passe'
    );
  }
}

function displayName(user: AdminUserView): string {
  const parts = [user.firstName, user.lastName].filter(Boolean);
  return parts.length > 0 ? parts.join(' ') : '—';
}

function initials(user: AdminUserView): string {
  const a = (user.firstName?.trim().charAt(0) ?? '').toUpperCase();
  const b = (user.lastName?.trim().charAt(0) ?? '').toUpperCase();
  if (a || b) return `${a}${b}` || a || b;
  return (user.email.charAt(0) || '?').toUpperCase();
}

function driversSummary(user: AdminUserView): string {
  if (user.drivers.length === 0) return '—';
  if (user.drivers.length === 1) {
    const d = user.drivers[0];
    return `${d.firstName} ${d.lastName}`;
  }
  return `${user.drivers.length} pilotes`;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

interface ListCallbacks {
  onChanged: () => void;
  setStatus: (msg: string, kind?: 'ok' | 'error') => void;
}

interface TableCallbacks extends ListCallbacks {
  onToggle: (userId: number) => void;
}

export function mountAccountsApp(root: HTMLElement): void {
  root.innerHTML = '';
  root.className = 'app-view circuits-app';

  const { page, body } = createPageShell({
    title: 'Comptes',
    subtitle: 'Cliquez une ligne pour gérer le compte.',
    breadcrumbs: [{ label: 'Tableau de bord', href: '#dashboard' }, { label: 'Comptes' }],
    wide: true,
  });
  page.classList.add('page--accounts');

  if (getCurrentUser()?.role !== 'admin') {
    body.appendChild(
      el('section', {
        class: 'accounts-gate',
        html: '<p>Accès réservé aux administrateurs.</p>',
      })
    );
    root.appendChild(page);
    return;
  }

  const toolbar = el('div', { class: 'accounts-toolbar' });
  const searchWrap = el('div', { class: 'accounts-search brutalist-container' });
  const searchInput = document.createElement('input');
  searchInput.type = 'search';
  searchInput.className = 'brutalist-input accounts-search-input';
  searchInput.placeholder = 'EMAIL, PRÉNOM, NOM…';
  searchInput.setAttribute('aria-label', 'Rechercher un compte');
  searchInput.autocomplete = 'off';
  searchInput.id = 'accounts-search';
  const searchLabel = el('label', { class: 'brutalist-label', text: 'Recherche' });
  searchLabel.setAttribute('for', 'accounts-search');
  searchWrap.append(searchInput, searchLabel);

  const statusLine = el('p', { class: 'accounts-status' });
  statusLine.setAttribute('aria-live', 'polite');
  toolbar.append(searchWrap, statusLine);

  const listHost = el('div', { class: 'accounts-list-host' });
  listHost.innerHTML = '<p class="accounts-loading">Chargement des comptes…</p>';

  body.append(toolbar, listHost);
  root.appendChild(page);

  let debounceTimer: ReturnType<typeof setTimeout> | null = null;
  let requestId = 0;
  let users: AdminUserView[] = [];
  let expandedId: number | null = null;

  const setStatus = (msg: string, kind: 'ok' | 'error' = 'ok'): void => {
    statusLine.textContent = msg;
    statusLine.classList.toggle('accounts-status--error', kind === 'error');
  };

  const paint = (): void => {
    renderTable(listHost, users, expandedId, {
      onToggle: (userId) => {
        expandedId = expandedId === userId ? null : userId;
        paint();
      },
      onChanged: () => load(searchInput.value),
      setStatus,
    });
  };

  const load = (query: string): void => {
    const id = ++requestId;
    listHost.innerHTML = '<p class="accounts-loading">Chargement…</p>';
    void fetchUsers(query)
      .then((next) => {
        if (id !== requestId) return;
        users = next;
        if (expandedId != null && !users.some((u) => u.id === expandedId)) {
          expandedId = null;
        }
        paint();
        setStatus(
          users.length === 0
            ? 'Aucun compte trouvé'
            : `${users.length} compte${users.length > 1 ? 's' : ''}`
        );
      })
      .catch((err: Error) => {
        if (id !== requestId) return;
        listHost.innerHTML = `<p class="auth-error">${err.message}</p>`;
        setStatus(err.message, 'error');
      });
  };

  searchInput.addEventListener('input', () => {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => load(searchInput.value), 280);
  });

  load('');
}

function renderTable(
  host: HTMLElement,
  users: AdminUserView[],
  expandedId: number | null,
  cb: TableCallbacks
): void {
  host.innerHTML = '';
  if (users.length === 0) {
    host.appendChild(
      el('div', {
        class: 'accounts-empty',
        html: '<p class="accounts-empty__title">Aucun résultat</p><p class="accounts-empty__hint">Essayez un autre email, prénom ou nom.</p>',
      })
    );
    return;
  }

  const wrap = el('div', { class: 'accounts-table-wrap' });
  const table = el('table', { class: 'data-table accounts-table' });

  const thead = document.createElement('thead');
  thead.innerHTML =
    '<tr><th class="accounts-col-expand" aria-hidden="true"></th><th>Nom</th><th>Email</th><th>Rôle</th><th>Statut</th><th>Pilotes</th></tr>';
  table.appendChild(thead);

  const tbody = document.createElement('tbody');
  for (const user of users) {
    const open = expandedId === user.id;
    tbody.appendChild(renderSummaryRow(user, open, cb));
    if (open) tbody.appendChild(renderDetailRow(user, cb));
  }
  table.appendChild(tbody);
  wrap.appendChild(table);
  host.appendChild(wrap);
}

function renderSummaryRow(
  user: AdminUserView,
  open: boolean,
  cb: TableCallbacks
): HTMLTableRowElement {
  const row = document.createElement('tr');
  row.className = `accounts-row${open ? ' accounts-row--open' : ''}${user.banned ? ' accounts-row--banned' : ''}`;
  row.tabIndex = 0;
  row.setAttribute('aria-expanded', open ? 'true' : 'false');

  const expandCell = document.createElement('td');
  expandCell.className = 'accounts-col-expand';
  expandCell.innerHTML = '<span class="accounts-expand-icon" aria-hidden="true"></span>';

  const nameCell = document.createElement('td');
  nameCell.className = 'accounts-col-name';
  nameCell.innerHTML = `
    <span class="accounts-row-identity">
      <span class="accounts-row-avatar" aria-hidden="true">${escapeHtml(initials(user))}</span>
      <span class="accounts-row-name">${escapeHtml(displayName(user))}</span>
    </span>
  `;

  const emailCell = document.createElement('td');
  emailCell.className = 'accounts-col-email';
  emailCell.textContent = user.email;

  const roleCell = document.createElement('td');
  roleCell.className = 'accounts-col-role';
  roleCell.textContent = ROLE_LABELS[user.role];

  const statusCell = document.createElement('td');
  statusCell.className = 'accounts-col-status';
  statusCell.innerHTML = user.banned
    ? '<span class="accounts-badge accounts-badge--banned">Banni</span>'
    : '<span class="accounts-badge accounts-badge--ok">Actif</span>';

  const driversCell = document.createElement('td');
  driversCell.className = 'accounts-col-drivers';
  driversCell.textContent = driversSummary(user);

  row.append(expandCell, nameCell, emailCell, roleCell, statusCell, driversCell);

  const toggle = (): void => cb.onToggle(user.id);
  row.addEventListener('click', toggle);
  row.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggle();
    }
  });

  return row;
}

function renderDetailRow(user: AdminUserView, cb: ListCallbacks): HTMLTableRowElement {
  const row = document.createElement('tr');
  row.className = 'accounts-detail-row';
  const cell = document.createElement('td');
  cell.colSpan = 6;
  cell.className = 'accounts-detail-cell';
  cell.appendChild(renderDetailPanel(user, cb));
  row.appendChild(cell);
  return row;
}

function renderDetailPanel(user: AdminUserView, cb: ListCallbacks): HTMLElement {
  const me = getCurrentUser();
  const panel = el('div', { class: 'accounts-detail' });

  const driversBlock = el('div', { class: 'accounts-detail__block' });
  driversBlock.appendChild(
    el('h3', { class: 'accounts-detail__title', text: 'Pilotes rattachés' })
  );
  const chips = el('div', { class: 'accounts-user__chips' });
  if (user.drivers.length === 0) {
    chips.appendChild(el('span', { class: 'accounts-chip accounts-chip--muted', text: 'Aucun' }));
  } else {
    for (const d of user.drivers) chips.appendChild(driverChip(d));
  }
  driversBlock.appendChild(chips);

  const controls = el('div', { class: 'accounts-detail__controls' });

  const roleField = el('div', { class: 'accounts-user__role' });
  roleField.appendChild(el('span', { class: 'accounts-user__field-label', text: 'Rôle' }));
  roleField.addEventListener('click', (e) => e.stopPropagation());
  const select = document.createElement('select');
  select.className = 'select accounts-role-select';
  select.setAttribute('aria-label', `Rôle de ${user.email}`);
  for (const role of ROLES) {
    const opt = document.createElement('option');
    opt.value = role;
    opt.textContent = ROLE_LABELS[role];
    opt.selected = role === user.role;
    select.appendChild(opt);
  }
  select.addEventListener('change', () => {
    select.disabled = true;
    void updateUserRole(user.id, select.value as UserRole)
      .then(() => {
        cb.setStatus(`Rôle mis à jour — ${user.email}`);
        cb.onChanged();
      })
      .catch((err: Error) => {
        select.value = user.role;
        cb.setStatus(err.message, 'error');
        window.alert(err.message);
      })
      .finally(() => {
        select.disabled = false;
      });
  });
  roleField.appendChild(select);
  enhanceMenuSelect(select);

  const actions = el('div', { class: 'accounts-user__actions' });
  actions.addEventListener('click', (e) => e.stopPropagation());

  const pwdBtn = el('button', {
    class: 'btn btn-ghost btn-sm',
    text: 'Mot de passe',
  }) as HTMLButtonElement;
  pwdBtn.type = 'button';

  const banBtn = el('button', {
    class: user.banned ? 'btn btn-primary btn-sm' : 'btn btn-danger btn-sm',
    text: user.banned ? 'Débannir' : 'Bannir',
  }) as HTMLButtonElement;
  banBtn.type = 'button';
  banBtn.disabled = me?.id === user.id;
  if (me?.id === user.id) banBtn.title = 'Vous ne pouvez pas vous bannir';

  const pwdPanel = el('form', { class: 'accounts-pwd-panel' }) as HTMLFormElement;
  pwdPanel.hidden = true;
  pwdPanel.addEventListener('click', (e) => e.stopPropagation());
  pwdPanel.innerHTML = `
    <div class="field brutalist-container">
      <input class="brutalist-input" type="password" name="password" minlength="8" required
        placeholder="8 CARACTÈRES MIN." autocomplete="new-password" />
      <label class="brutalist-label">Nouveau mot de passe</label>
    </div>
    <div class="accounts-pwd-panel__actions">
      <button type="submit" class="btn btn-primary btn-sm">Enregistrer</button>
      <button type="button" class="btn btn-ghost btn-sm" data-cancel>Annuler</button>
    </div>
    <p class="auth-error accounts-pwd-panel__error" role="alert" hidden></p>
  `;

  pwdBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    pwdPanel.hidden = !pwdPanel.hidden;
    if (!pwdPanel.hidden) {
      pwdPanel.querySelector<HTMLInputElement>('input[name="password"]')?.focus();
    }
  });
  pwdPanel.querySelector('[data-cancel]')?.addEventListener('click', () => {
    pwdPanel.hidden = true;
    pwdPanel.reset();
  });
  pwdPanel.addEventListener('submit', (e) => {
    e.preventDefault();
    e.stopPropagation();
    const input = pwdPanel.querySelector<HTMLInputElement>('input[name="password"]');
    const errorEl = pwdPanel.querySelector<HTMLElement>('.accounts-pwd-panel__error');
    const password = input?.value ?? '';
    if (errorEl) {
      errorEl.hidden = true;
      errorEl.textContent = '';
    }
    if (password.length < 8) {
      if (errorEl) {
        errorEl.textContent = 'Au moins 8 caractères.';
        errorEl.hidden = false;
      }
      return;
    }
    const submitBtn = pwdPanel.querySelector<HTMLButtonElement>('button[type="submit"]');
    if (submitBtn) submitBtn.disabled = true;
    void updateUserPassword(user.id, password)
      .then(() => {
        cb.setStatus(`Mot de passe mis à jour — ${user.email}`);
        pwdPanel.hidden = true;
        pwdPanel.reset();
      })
      .catch((err: Error) => {
        if (errorEl) {
          errorEl.textContent = err.message;
          errorEl.hidden = false;
        }
        cb.setStatus(err.message, 'error');
      })
      .finally(() => {
        if (submitBtn) submitBtn.disabled = false;
      });
  });

  banBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    const next = !user.banned;
    const label = next ? 'bannir' : 'débannir';
    if (!window.confirm(`Confirmer : ${label} ${user.email} ?`)) return;
    banBtn.disabled = true;
    void updateUserBanned(user.id, next)
      .then(() => {
        cb.setStatus(`${user.email} ${next ? 'banni' : 'débanni'}`);
        cb.onChanged();
      })
      .catch((err: Error) => {
        cb.setStatus(err.message, 'error');
        window.alert(err.message);
        banBtn.disabled = false;
      });
  });

  actions.append(pwdBtn, banBtn);
  controls.append(roleField, actions);
  panel.append(driversBlock, controls, pwdPanel);
  return panel;
}

function driverChip(d: AdminUserDriver): HTMLElement {
  const a = document.createElement('a');
  a.href = `#garage/pilotes/${d.id}`;
  a.className = 'accounts-chip';
  a.append(
    document.createTextNode(`${d.firstName} ${d.lastName}`),
    el('span', { class: 'accounts-chip__div', text: `D${d.division}` })
  );
  a.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    window.location.hash = `garage/pilotes/${d.id}`;
  });
  return a;
}
